import React, { useCallback, useEffect, useState } from "react";
import { KeyRound, Mail, MessageSquare, ShieldCheck, Smartphone } from "lucide-react";
import { toast } from "react-toastify";
import { membersApi, membersErr, SignInSettings as Settings } from "../../api/members/membersApi";

const FACTORS: { key: string; label: string; hint: string; icon: React.ReactNode }[] = [
  { key: "totp", label: "Authenticator app", hint: "Google/Microsoft Authenticator, Authy, 1Password", icon: <Smartphone size={16} /> },
  { key: "u2f", label: "Security key / passkey", hint: "Hardware key or device fingerprint / face unlock", icon: <KeyRound size={16} /> },
  { key: "otp_email", label: "Email code", hint: "One-time code sent to the member's verified email", icon: <Mail size={16} /> },
  { key: "otp_sms", label: "SMS code", hint: "Not available on this platform yet", icon: <MessageSquare size={16} /> },
];

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string; hint?: string }> = ({
  checked,
  onChange,
  disabled,
  label,
  hint,
}) => (
  <label className="acct-row" style={{ cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1 }}>
    <div className="acct-row__main">
      <div>
        <div className="acct-row__label">{label}</div>
        {hint && <div className="acct-row__hint">{hint}</div>}
      </div>
    </div>
    <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} style={{ width: 18, height: 18 }} />
  </label>
);

/**
 * Settings → Members → Sign-in methods. The org's Zitadel login policy:
 * password on/off (SSO-only orgs), forgot-password link, required 2FA and
 * which second factors members may enrol. The sign-in page and My Account
 * follow these live.
 */
const SignInSettings: React.FC<{ tenantId: string; refreshKey?: number }> = ({ tenantId, refreshKey }) => {
  const [saved, setSaved] = useState<Settings | null>(null);
  const [form, setForm] = useState<Settings | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!tenantId) return;
    setError("");
    try {
      const r = await membersApi.signInSettings(tenantId);
      setSaved(r.data);
      setForm(r.data);
    } catch (e) {
      setError(membersErr(e, "Could not load sign-in settings"));
    }
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  if (!form || !saved) {
    return (
      <div className="acct-card" style={{ marginTop: 20 }}>
        <div className="acct-card__head"><div><h3>Sign-in methods</h3></div></div>
        {error ? <div className="acct-notice acct-notice--error">{error}</div> : <p className="acct-loading">Loading...</p>}
      </div>
    );
  }

  const dirty = JSON.stringify(form) !== JSON.stringify(saved);
  const toggleFactor = (k: string) =>
    setForm({ ...form, second_factors: form.second_factors.includes(k) ? form.second_factors.filter((x) => x !== k) : [...form.second_factors, k] });

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const r = await membersApi.updateSignInSettings(tenantId, {
        allow_password: form.allow_password,
        password_reset: form.password_reset,
        force_mfa: form.force_mfa,
        force_mfa_local_only: form.force_mfa_local_only,
        second_factors: form.second_factors,
      });
      setSaved(r.data);
      setForm(r.data);
      toast.success("Sign-in settings saved");
    } catch (e) {
      setError(membersErr(e, "Could not save sign-in settings"));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset sign-in methods to ChargingAdda defaults?")) return;
    setBusy(true);
    setError("");
    try {
      const r = await membersApi.resetSignInSettings(tenantId);
      setSaved(r.data);
      setForm(r.data);
      toast.success("Reset to defaults");
    } catch (e) {
      setError(membersErr(e, "Could not reset"));
    } finally {
      setBusy(false);
    }
  };

  const noSso = saved.sso_providers === 0;

  return (
    <div className="acct-card" style={{ marginTop: 20 }}>
      <div className="acct-card__head">
        <div>
          <h3>Sign-in methods</h3>
          <p>How members of <strong>this admin dashboard</strong> may sign in and what two-factor authentication is required.</p>
        </div>
        {saved.customised ? (
          <span className="acct-badge acct-badge--muted">Customised</span>
        ) : (
          <span className="acct-badge acct-badge--muted">ChargingAdda defaults</span>
        )}
      </div>

      <Toggle
        label="Password sign-in"
        hint={noSso ? "Cannot be turned off until a single sign-on provider is connected and enabled." : "Turn off to make this dashboard SSO-only."}
        checked={form.allow_password}
        disabled={noSso}
        onChange={(v) => setForm({ ...form, allow_password: v })}
      />
      <Toggle
        label="Show “Forgot password?”"
        hint="Lets members request a reset link by email."
        checked={form.password_reset}
        disabled={!form.allow_password}
        onChange={(v) => setForm({ ...form, password_reset: v })}
      />
      <Toggle
        label="Require two-factor authentication"
        hint="Members without a second factor are asked to set one up at their next sign-in."
        checked={form.force_mfa}
        onChange={(v) => setForm({ ...form, force_mfa: v, force_mfa_local_only: v ? form.force_mfa_local_only : false })}
      />
      {form.force_mfa && (
        <Toggle
          label="…only for password sign-ins"
          hint="Members who sign in through single sign-on are exempt; their identity provider handles it."
          checked={form.force_mfa_local_only}
          onChange={(v) => setForm({ ...form, force_mfa_local_only: v })}
        />
      )}

      <div className="acct-row" style={{ display: "block" }}>
        <div className="acct-row__label" style={{ marginBottom: 8 }}>
          <ShieldCheck size={16} /> Allowed second factors
        </div>
        <div className="role-picker">
          {FACTORS.map((f) => {
            const unavailable = f.key === "otp_sms" && !saved.sms_available;
            return (
              <label key={f.key} style={{ opacity: unavailable ? 0.55 : 1 }}>
                <input type="checkbox" checked={form.second_factors.includes(f.key)} disabled={unavailable} onChange={() => toggleFactor(f.key)} />
                <span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>{f.icon} {f.label}</span>
                  <small>{f.hint}</small>
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {error && <div className="acct-notice acct-notice--error" style={{ marginTop: 10 }}>{error}</div>}

      <div className="acct-actions">
        {saved.customised && saved.sso_providers === 0 && (
          <button type="button" className="acct-btn" disabled={busy} onClick={reset} style={{ marginRight: "auto" }}>
            Reset to defaults
          </button>
        )}
        <button type="button" className="acct-btn" disabled={busy || !dirty} onClick={() => setForm(saved)}>
          Discard
        </button>
        <button type="button" className="acct-btn acct-btn--primary" disabled={busy || !dirty} onClick={save}>
          {busy ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default SignInSettings;
